import React from "react";
import PropTypes from "prop-types";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import ReactTable from "react-table";
import { Button, ButtonGroup } from "react-bootstrap";
import { Popconfirm } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { closeCustomModal, openCustomModal } from "components/custom/modals";
import ReactTableFilterSelect from "components/custom/forms/react-table-filter-select";
import filterCaseInsensitive from "components/custom/forms/react-table-case-insensitive";

import {
    fetchProducts,
    deleteProduct,
    clearProducts
} from "api/menu/master-files/product-maintenance/actions";

import {
    fetchConsumerGroups,
    clearConsumerGroups
} from "api/menu/master-files/consumer-groups/actions";

// import ProductEditModal from
// "components/menu/master-files/product-maintenance/create-edit-modal";
const ProductEditModal = React.lazy(() =>
    import("components/menu/master-files/product-maintenance/create-edit-modal"));

class ProductsList extends React.Component {
    constructor(props) {
        super(props);

        this.modalOpen = this.modalOpen.bind(this);
    }

    componentDidMount () {
        this.props.fetchProducts(true);
        this.props.fetchConsumerGroups();
    }

    componentWillUnmount () {
        this.props.clearProducts();
        this.props.clearConsumerGroups();
    }

    modalOpen = (productId = false) => {
        const modalTitle = productId !== false ? "Update" : "Create";

        const modalObj = {
            id: `product-${productId}`,
            header: `${modalTitle} product`,
            children: (
                <React.Suspense fallback={<div className="m-2 text-center font-weight-bold">Loading...</div>}>
                    <ProductEditModal
                        productId={productId}
                        onCancel={() => this.props.closeCustomModal(modalObj)}
                    />
                </React.Suspense>
            ),
            handleClose: () => this.props.closeCustomModal(modalObj)
        };
        this.props.openCustomModal(modalObj);
    };

    delete = (productId, name) => {
        this.props.deleteProduct(productId, name, () => this.props.fetchProducts(true));
    };

    render () {
        const {
            products,
            loading,
            consumerGroups
        } = this.props;

        const classNameFlexCenter = "d-flex justify-content-center align-items-center";
        const columns = [
            {
                Header: "Product Code",
                accessor: "code",
                id: "code",
            },
            {
                Header: "Description 1",
                accessor: "description1",
                id: "description1",
            },
            {
                Header: "Consumer Group",
                accessor: "consumerGroupId",
                id: "consumerGroupId",
                Cell: ({ original }) => original.consumerGroup,
                Filter: ({ filter, onChange }) => {
                    const consumers = [];
                    consumerGroups.forEach((value) => {
                        consumers.push({
                            value: value.id,
                            label: value.code
                        });
                    });
                    return (
                        <select
                            onChange={event => onChange(event.target.value)}
                            style={{ width: "100%", height: "100%" }}
                            value={filter ? filter.value : ""}
                        >
                            <option value="">Show All</option>
                            {consumers.map((item) => (
                                <option key={item.value} value={item.value}>
                                    {item.label}
                                </option>
                            ))}
                        </select>
                    );
                }
            },
            {
                Header: "Active?",
                id: "isInactive",
                accessor: original => (original.isInactive ? "NO" : "YES"),
                Cell: ({ original }) => (original.isInactive ? "NO" : "YES"),
                className: classNameFlexCenter,
                Filter: ({ filter, onChange }) => (
                    <ReactTableFilterSelect
                        currentValue={filter ? filter.value : ""}
                        onChange={value => onChange(value)}
                        style={{ width: "100%", height: "100%" }}
                        optionsObject={{
                            "": "Show All",
                            YES: "Active",
                            NO: "Inactive"
                        }}
                    />
                )
            },
            {
                Header: "Actions",
                id: "actions",
                className: classNameFlexCenter,
                Cell: ({ original }) => (
                    <ButtonGroup>
                        <Button size="sm" onClick={() => this.modalOpen(original.id)}>
                            <FontAwesomeIcon icon="pen" title="Update" />
                        </Button>
                        <Popconfirm
                            title={`Delete "${original.code}"?`}
                            onConfirm={() => this.delete(original.id, original.code)}
                            placement="bottomRight"
                            okText="Yes"
                            cancelText="Cancel"
                        >
                            <Button variant="danger" size="sm">
                                <FontAwesomeIcon icon="times" />
                            </Button>
                        </Popconfirm>
                    </ButtonGroup>
                ),
                filterable: false,
                width: 100
            },
        ];

        return (
            <div className="dashboard">
                <h3 className="mb-4">
                    Product Maintenance
                </h3>

                <ReactTable
                    data={products}
                    headerClassName="table-header"
                    columns={columns}
                    defaultPageSize={20}
                    pageSizeOptions={[10, 20, 50, 100]}
                    resizable
                    className="-striped -highlight"
                    loading={loading}
                    filterable
                    defaultFilterMethod={filterCaseInsensitive}
                    defaultSorted={[
                        {
                            id: "code",
                            desc: false
                        }
                    ]}
                    defaultFiltered={[
                        {
                            id: "isInactive",
                            value: "YES"
                        }
                    ]}
                    getNoDataProps = {() => ({
                        style: {
                            zIndex: 0
                        }
                    })}
                    style={{
                        height: "calc(100vh - 200px)",
                    }}
                />

                <div className="mt-4 mb-4 text-right">
                    <Button className="ml-2" variant="primary" onClick={() => this.modalOpen()}>
                        Add New Product
                    </Button>
                </div>
            </div>
        );
    }
}

ProductsList.propTypes = {
    fetchProducts: PropTypes.func.isRequired,
    deleteProduct: PropTypes.func.isRequired,
    clearProducts: PropTypes.func.isRequired,

    fetchConsumerGroups: PropTypes.func.isRequired,
    clearConsumerGroups: PropTypes.func.isRequired,

    products: PropTypes.array.isRequired,
    loading: PropTypes.bool.isRequired,

    consumerGroups: PropTypes.array.isRequired,
};

const mapStateToProps = state => ({
    products: state.products.products,
    loading: state.products.loading,

    consumerGroups: state.consumerGroups.consumerGroups,
});

const mapStateToDispatch = dispatch => ({
    fetchProducts: bindActionCreators(fetchProducts, dispatch),
    deleteProduct: bindActionCreators(deleteProduct, dispatch),
    clearProducts: bindActionCreators(clearProducts, dispatch),

    fetchConsumerGroups: bindActionCreators(fetchConsumerGroups, dispatch),
    clearConsumerGroups: bindActionCreators(clearConsumerGroups, dispatch),

    openCustomModal: bindActionCreators(openCustomModal, dispatch),
    closeCustomModal: bindActionCreators(closeCustomModal, dispatch)
});

export default connect(
    mapStateToProps,
    mapStateToDispatch
)(ProductsList);
